import React, { useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertTriangle, ChevronDown, ChevronRight, Clock, PhoneCall, PhoneMissed, PhoneOff, Timer, UserX, Voicemail } from 'lucide-react';
import { InfoTip } from '@/pages/reports/_Components/InfoTip';
import BucketChip from '../components/BucketChip';
import HourStatusRow from '../components/HourStatusRow';
import NumLink from '../components/NumLink';
import RateBar from '../components/RateBar';
import StatCard from '../components/StatCard';
import { EMPTY, PANEL, SECTION_TITLE, TD, TH } from '../components/tableStyles';
import { useReport } from '../ReportContext';
import { adaptHourlyCalls } from '../adapt';
import { inr, num, pct, ratio } from '../format';
import { hourRange, useHourly } from '../hourly';
import { callTotals, mmss, totals } from '../stats';
import { BUCKETS, CHART, WORK_BUCKETS } from '../theme';
import type { CallRow, CallsView, OpenDrill, OrderBucketKey, WorkRow } from '../types';

interface Props { calls: CallRow[]; rows: WorkRow[]; openDrill: OpenDrill }

const VIEWS: { key: CallsView; label: string; hint: string }[] = [
  { key: 'bucket', label: 'By bucket', hint: 'Which workstream the calls were made for.' },
  { key: 'agent', label: 'By agent', hint: 'Reach, talk time and what that time earned.' },
  { key: 'hour', label: 'By hour of day', hint: 'When customers actually pick up.' },
];

const th = `${TH} text-right`;

const CallsTab: React.FC<Props> = ({ calls, rows, openDrill }) => {
  const { hourly: hourlyRaw, attempts: att, minTalkSeconds } = useReport();
  const [view, setView] = useState<CallsView>('bucket');
  const t = useMemo(() => callTotals(calls), [calls]);
  // Hour-of-day rows under each bucket (hour the call was placed), fetched the first time one is opened.
  const hourlyCalls = useHourly('calls', adaptHourlyCalls);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (b: string) => { setOpen(prev => { const n = new Set(prev); if (n.has(b)) n.delete(b); else n.add(b); return n; }); hourlyCalls.load(); };
  const agents = useMemo(() => Array.from(new Set(calls.map(c => c.agent))), [calls]);

  if (calls.length === 0) return <div className={EMPTY}>No calls for this selection.</div>;

  const drill = (title: string, agent: string, bucket: OrderBucketKey | 'all', total: number, callOutcome: 'connected' | 'not_connected' | 'all', hour?: number) => () =>
    openDrill({ title, kind: 'calls', agent, bucket, total, callOutcome, hour });

  const reasons = [
    { name: 'No answer', value: t.noAnswer, fill: '#c7d2fe' },
    { name: 'Cancelled while ringing', value: t.cancelledRinging, fill: '#fde68a' },
    { name: 'Dropped / network', value: t.droppedOther, fill: '#fdc9a0' },
    { name: `Hung up early (<${minTalkSeconds}s)`, value: t.hungUpEarly, fill: '#fecdd3' },
  ];
  const pc = (n: number) => (att.customers ? Math.round((100 * n) / att.customers) : 0);
  const attempts = [
    { name: 'Attempt 1', pct: pc(att.a1) }, { name: 'Attempt 2', pct: pc(att.a2) }, { name: 'Attempt 3', pct: pc(att.a3) },
    { name: 'Attempt 4+', pct: pc(att.a4plus) }, { name: 'Never reached', pct: pc(att.never) },
  ];
  const hourly = hourlyRaw.map(h => ({ hour: h.hour, 'Connect %': h.dials ? Math.round((100 * h.connected) / h.dials) : 0, Dials: h.dials }));
  const withDials = hourlyRaw.filter(h => h.dials >= 30);
  const rate = (h: { dials: number; connected: number }) => (h.dials ? h.connected / h.dials : 0);
  const best = withDials.length ? withDials.reduce((a, b) => (rate(b) > rate(a) ? b : a)) : null;
  const worst = withDials.length ? withDials.reduce((a, b) => (rate(b) < rate(a) ? b : a)) : null;

  // Agents worth a conversation: well below the team on reach, or unusually many very short calls.
  const perAgent = agents.map(a => ({ a, c: callTotals(calls.filter(x => x.agent === a)) }));
  const watch = perAgent.flatMap(({ a, c }) => {
    const flags: string[] = [];
    if (t.connectRate !== null && c.connectRate !== null && c.connectRate < t.connectRate - 8) flags.push(`connect rate ${c.connectRate}% vs team ${t.connectRate}%`);
    if (t.shortRate !== null && c.shortRate !== null && c.shortRate > t.shortRate * 1.6 && c.shortRate > 25) flags.push(`${c.shortRate}% of connected calls under 30s`);
    return flags.length ? [{ agent: a, flags }] : [];
  });

  const rowFor = (label: React.ReactNode, key: string, c: ReturnType<typeof callTotals>, agent: string, bucket: OrderBucketKey | 'all', extra?: React.ReactNode, hour?: number) => (
    <tr key={key} className={bucket !== 'all' ? BUCKETS[bucket].tint : ''}>
      <td className={TD}>{label}</td>
      <td className={`${TD} text-right`}><NumLink text={num(c.dials)} onClick={drill(`Dials — ${key}`, agent, bucket, c.dials, 'all', hour)} /></td>
      <td className={`${TD} text-right`}><NumLink text={num(c.connected)} zero={c.connected === 0} onClick={drill(`Connected — ${key}`, agent, bucket, c.connected, 'connected', hour)} /></td>
      <td className={TD}><RateBar value={c.connectRate} /></td>
      <td className={`${TD} text-right`}><NumLink text={num(c.notConnected)} zero={c.notConnected === 0} onClick={drill(`Not connected — ${key}`, agent, bucket, c.notConnected, 'not_connected', hour)} /></td>
      <td className={`${TD} text-right tabular-nums text-slate-700`}>{pct(c.notConnectedRate)}</td>
      <td className={`${TD} text-right tabular-nums`}>{c.talkHours}</td>
      <td className={`${TD} text-right tabular-nums`}>{mmss(c.avgTalk)}</td>
      <td className={`${TD} text-right tabular-nums`}>{pct(c.shortRate)}</td>
      <td className={`${TD} text-right tabular-nums`}>{num(c.customersCalled)}</td>
      <td className={`${TD} text-right tabular-nums`}>{num(c.neverConnected)} <span className="text-xs text-slate-600">({pct(c.neverRate)})</span></td>
      {extra}
    </tr>
  );

  const head = (first: string, extra?: React.ReactNode) => (
    <thead className="bg-slate-50/80 dark:bg-slate-800"><tr>
      <th className={`${TH} text-left`}>{first}</th>
      <th className={th}>Dials<InfoTip text="Every outbound call attempt, answered or not." /></th>
      <th className={th}>Connected</th><th className={th}>Connect %</th>
      <th className={th}>Not connected<InfoTip text="Called, but the customer did not pick up or the call failed. Reasons are in the panel below." /></th>
      <th className={th}>Not conn. %</th>
      <th className={th}>Talk hours</th><th className={th}>Avg talk<InfoTip text="Average talk time per connected call (minutes:seconds)." /></th>
      <th className={th}>Short calls<InfoTip text="Connected calls under 30 seconds. A high share can mean wrong numbers, hang-ups or agents ending calls early." /></th>
      <th className={th}>Customers called</th>
      <th className={th}>Never reached<InfoTip text="Customers dialled at least once and never connected in this period. These are the leads nobody has actually spoken to." /></th>
      {extra}
    </tr></thead>
  );

  return (
    <div className="space-y-6">
      <section>
        <h2 className={`${SECTION_TITLE} mb-2`}>Are we reaching customers?</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Total dials" value={num(t.dials)} tone="sky" icon={<PhoneCall className="h-4 w-4" />}
            sub={`${t.attemptsPerCustomer ?? '—'} dials per customer`} onClick={drill('All dials', 'all', 'all', t.dials, 'all')} />
          <StatCard label="Connected" value={num(t.connected)} tone="mint" icon={<PhoneCall className="h-4 w-4" />}
            info="The customer picked up and talked. The minimum talk time that counts is a business setting."
            sub={`${pct(t.connectRate)} of dials`} onClick={drill('Connected calls', 'all', 'all', t.connected, 'connected')} />
          <StatCard label="Called, not connected" value={num(t.notConnected)} tone="rose" icon={<PhoneMissed className="h-4 w-4" />}
            info="Dialled but not connected: no answer, switched off, busy, invalid number or hung up in the first seconds."
            sub={`${pct(t.notConnectedRate)} of dials`} onClick={drill('Not connected calls', 'all', 'all', t.notConnected, 'not_connected')} />
          <StatCard label="Customers never reached" value={num(t.neverConnected)} tone="butter" icon={<UserX className="h-4 w-4" />}
            info="Called at least once, never connected in this period." sub={`${pct(t.neverRate)} of ${num(t.customersCalled)} customers called`} />
        </div>
      </section>

      <section>
        <h2 className={`${SECTION_TITLE} mb-2`}>How much time are we talking?</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <StatCard label="Hours talked" value={`${num(t.talkHours)} h`} tone="lavender" icon={<Clock className="h-4 w-4" />} sub={`${Math.round(t.talkHours / Math.max(t.activeDays, 1) * 10) / 10} h per day, whole team`} />
          <StatCard label="Avg talk time" value={mmss(t.avgTalk)} tone="peach" icon={<Timer className="h-4 w-4" />} info="Per connected call." sub="minutes:seconds" />
          <StatCard label="Avg ring time" value={`${t.avgRing ?? '—'} s`} tone="sky" icon={<Voicemail className="h-4 w-4" />} info="How long the phone rings before an answer or give-up. Long ring times waste agent minutes." sub="per dial" />
          <StatCard label="Short calls" value={pct(t.shortRate)} tone="rose" icon={<PhoneOff className="h-4 w-4" />} info="Connected calls under 30 seconds." sub={`${num(t.shortCalls)} calls`} />
          <StatCard label="Dials per day" value={num(Math.round(t.dials / Math.max(t.activeDays, 1)))} tone="mint" icon={<PhoneCall className="h-4 w-4" />} sub={`over ${t.activeDays} working days`} />
        </div>
      </section>

      {watch.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Worth a conversation</p>
            <ul className="mt-1 list-disc pl-5">
              {watch.map(w => <li key={w.agent}><b>{w.agent}</b>: {w.flags.join('; ')}</li>)}
            </ul>
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-200/80">Flags are a prompt to look at the calls, not a verdict. Different buckets connect at very different rates.</p>
          </div>
        </div>
      )}

      <div className={PANEL}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Call views">
            {VIEWS.map(v => (
              <button key={v.key} type="button" role="tab" aria-selected={view === v.key} onClick={() => setView(v.key)}
                className={`rounded-full px-3 py-1 text-sm ${view === v.key
                  ? 'bg-sky-100 text-sky-800 dark:bg-sky-400/20 dark:text-sky-100'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'}`}>{v.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {view === 'bucket' && hourlyCalls.available && (
              <div className="flex gap-2 text-sm">
                <button type="button" onClick={() => { setOpen(new Set(([...WORK_BUCKETS, 'none'] as string[]).filter(b => calls.some(c => c.bucket === b)))); hourlyCalls.load(); }} className="rounded-lg px-2.5 py-1 text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-400/10">Expand all hours</button>
                <button type="button" onClick={() => setOpen(new Set())} className="rounded-lg px-2.5 py-1 text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">Collapse all</button>
              </div>
            )}
            <p className="text-xs text-slate-600">{VIEWS.find(v => v.key === view)?.hint}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          {view === 'bucket' && (
            <table className="min-w-full">
              {head('Bucket')}
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
                {([...WORK_BUCKETS, 'none'] as const).filter(b => calls.some(c => c.bucket === b)).map(b => {
                  const name = b === 'none' ? 'No open work' : BUCKETS[b].label;
                  const chip = <BucketChip bucket={b} label={b === 'none' ? 'No open work' : undefined} />;
                  const isOpen = hourlyCalls.available && open.has(b);
                  const hours = hourlyCalls.data?.[b] ?? [];
                  return (
                    <React.Fragment key={b}>
                      {rowFor(hourlyCalls.available ? (
                        <button type="button" onClick={() => toggle(b)} aria-expanded={isOpen} title="Hourly breakdown" className="flex items-center gap-1.5">
                          {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-600" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-600" />}
                          {chip}
                        </button>
                      ) : chip, name, callTotals(calls.filter(c => c.bucket === b)), 'all', b)}
                      {isOpen && <HourStatusRow state={hourlyCalls} colSpan={11} hasRows={hours.length > 0} />}
                      {isOpen && hours.map(({ hour, row }) => rowFor(
                        <span className="pl-6">{hourRange(hour)}</span>, `${name} ${hourRange(hour)}`, callTotals([row]), 'all', b, undefined, hour))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-slate-200 bg-slate-50/80 font-semibold dark:border-slate-600 dark:bg-slate-800">
                {rowFor('All buckets', 'All buckets', t, 'all', 'all')}
              </tfoot>
            </table>
          )}
          {view === 'agent' && (
            <table className="min-w-full">
              {head('Agent', <>
                <th className={th}>Dials / day</th>
                <th className={th}>Talk h / day<InfoTip text="Hours on connected calls per working day. Compare with the length of a shift to see how much of the day is spent talking." /></th>
                <th className={th}>Revenue per talk hour<InfoTip text="Valid revenue credited to the agent divided by hours talked. A rough productivity measure, not a target." /></th>
              </>)}
              <tbody className="divide-y divide-slate-50 dark:divide-slate-700/60">
                {perAgent.map(({ a, c }) => {
                  const rev = totals(rows.filter(r => r.agent === a)).validRevenue;
                  return rowFor(<span className="font-medium">{a}</span>, a, c, a, 'all', <>
                    <td className={`${TD} text-right tabular-nums`}>{Math.round(c.dials / Math.max(c.activeDays, 1))}</td>
                    <td className={`${TD} text-right tabular-nums`}>{Math.round((c.talkHours / Math.max(c.activeDays, 1)) * 10) / 10}</td>
                    <td className={`${TD} text-right tabular-nums`}>{c.talkHours ? inr(Math.round(rev / c.talkHours)) : '—'}</td>
                  </>);
                })}
              </tbody>
            </table>
          )}
          {view === 'hour' && (
            <div className="p-4">
              <p className="mb-2 text-sm text-slate-700 dark:text-slate-200">
                {best && worst ? (
                  <>Best hour to call: <b>{best.hour}</b> ({Math.round(100 * rate(best))}% connect). Weakest: <b>{worst.hour}</b> ({Math.round(100 * rate(worst))}%).
                  Moving dials from weak hours to strong ones raises reach without any extra calling time.</>
                ) : 'Not enough calls in this selection to compare hours.'}
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={hourly} margin={{ left: -10 }}>
                    <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 12 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip /><Legend />
                    <Bar yAxisId="l" dataKey="Dials" fill={CHART.received} radius={[6, 6, 0, 0]} />
                    <Line yAxisId="r" type="monotone" dataKey="Connect %" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={`${PANEL} p-4`}>
          <h3 className={`${SECTION_TITLE} mb-1`}>Why calls did not connect</h3>
          <p className="mb-2 text-xs text-slate-600">Each dial that did not connect, by the dialer&apos;s own reason. {pct(ratio(t.cancelledRinging, t.notConnected))} were cancelled while ringing.</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reasons} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                <Tooltip />
                <Bar dataKey="value" name="Calls" radius={[0, 6, 6, 0]}>
                  {reasons.map(r => <Cell key={r.name} fill={r.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={`${PANEL} p-4`}>
          <h3 className={`${SECTION_TITLE} mb-1`}>How many attempts to reach a customer</h3>
          <p className="mb-2 text-xs text-slate-600">Share of customers called, by the attempt on which they first connected.</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={attempts} margin={{ left: -10 }}>
                <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} unit="%" />
                <Tooltip />
                <Bar dataKey="pct" name="Customers" fill={CHART.worked} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
};

export default CallsTab;
